import { InteractionRequiredAuthError } from "@azure/msal-browser";
import type { AccountInfo } from "@azure/msal-browser";
import { msalInstance, apiRequest } from "../config/msalConfig";
import { getLocalAccessToken } from "./localAuthStorage";
import { notifyUnauthorized } from "./authSession";

/**
 * Silent MSAL token acquisition shared by every concurrent caller, so a burst
 * of API requests results in a single `acquireTokenSilent` call.
 */
let inFlightMsalToken: Promise<string> | null = null;

/**
 * Set once the session-expired flow has been triggered for an interaction
 * required error; cleared after the next successful silent acquisition.
 */
let sessionExpiryNotified = false;

async function acquireMsalTokenSilently(account: AccountInfo): Promise<string> {
  if (!msalInstance || !apiRequest) {
    throw new Error("MSAL instance not available");
  }

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
    });

    sessionExpiryNotified = false;
    return tokenResponse.accessToken;
  } catch (error) {
    // Never open a popup/redirect from a background request: show the login
    // screen instead, where the user signs in again interactively.
    if (error instanceof InteractionRequiredAuthError && !sessionExpiryNotified) {
      sessionExpiryNotified = true;
      notifyUnauthorized("session_expired");
    }

    throw error;
  }
}

export async function getAccessToken(): Promise<string> {
  const localToken = getLocalAccessToken();
  if (localToken) {
    return localToken;
  }

  const isMsalEnabled = import.meta.env.VITE_MSAL_ENABLED === "true";

  if (!isMsalEnabled) {
    return "xx";
  }

  if (!msalInstance) {
    throw new Error("MSAL instance not available");
  }

  if (!apiRequest) {
    throw new Error("MSAL API request not available");
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error("No signed-in account");
  }

  if (!inFlightMsalToken) {
    inFlightMsalToken = acquireMsalTokenSilently(accounts[0]).finally(() => {
      inFlightMsalToken = null;
    });
  }

  return inFlightMsalToken;
}
